<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n, Translation } from 'vue-i18n'
import { usePwaInstall } from '@/composables/pwa'
import { useUserStore } from '@/stores/user'
import { getStorageItem, setStorageItem } from '@/modules/native'
import IconButton from '@/components/IconButton.vue'

const userStore = useUserStore()

const { t } = useI18n()
const {
  canInstall,
  isInstalled,
  isIOS,
  isGuideShown,
  promptInstall,
  closeGuide
} = usePwaInstall()

const isDismissed = ref(false)

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

const checkDismissed = async () => {
  const dismissedUntil = await getStorageItem('installPromptDismissed')
  if (dismissedUntil) {
    const expiresAt = Number(dismissedUntil)
    if (Date.now() < expiresAt) {
      isDismissed.value = true
    }
  }
}

onMounted(() => {
  checkDismissed()
})

const dismissPrompt = async () => {
  isDismissed.value = true
  const expiresAt = Date.now() + DISMISS_DURATION_MS
  await setStorageItem('installPromptDismissed', expiresAt.toString())
}
</script>

<template>
  <div v-if="userStore.auth.user && !isInstalled">
    <Transition
      enter-active-class="transform transition duration-500 ease-out"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transform transition duration-300 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="!isDismissed && (canInstall || isIOS)"
        class="fixed bottom-16 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 bg-gray-800 shadow-2xl rounded-2xl p-4 border border-gray-700"
      >
        <div class="flex items-center gap-4">
          <img
            src="/img/icons/rcz-512x512.png"
            alt="RCZ"
            class="w-12 h-12 flex-shrink-0"
          />
          <div class="flex-1 min-w-0">
            <h4 class="font-bold text-white leading-tight">
              {{ t('install.title') }}
            </h4>
            <p class="text-xs text-gray-400">
              {{ t('install.description') }}
            </p>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
              @click="promptInstall"
            >
              {{ t('install.prompt') }}
            </button>
            <IconButton
              class="text-gray-400 hover:text-white"
              type="simple"
              icon="x"
              size="base"
              :title="t('install.dismiss')"
              @click="dismissPrompt"
            />
          </div>
        </div>
      </div>
    </Transition>

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isGuideShown"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end"
        @click="closeGuide"
      >
        <div
          class="w-full bg-white dark:bg-gray-900 rounded-t-3xl p-6 pb-10 shadow-xl animate-in slide-in-from-bottom duration-300"
          @click.stop
        >
          <div
            class="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-6"
          />

          <h3 class="text-xl font-bold mb-4 dark:text-white">
            {{ t('iphone.install') }}
          </h3>

          <div class="space-y-6">
            <div class="flex items-start gap-4">
              <span
                class="bg-gray-800 w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                >1</span
              >
              <p class="text-gray-300">
                <Translation keypath="iphone.instructions.step1.text">
                  <template #highlight>
                    <span class="font-bold text-cyan-600 italic">{{
                      t('iphone.instructions.step1.highlight')
                    }}</span>
                  </template>
                </Translation>
              </p>
            </div>

            <div class="flex items-start gap-4">
              <span
                class="bg-gray-800 w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                >2</span
              >
              <p class="text-gray-300">
                <Translation keypath="iphone.instructions.step2.text">
                  <template #highlight>
                    <span class="font-bold underline uppercase">{{
                      t('iphone.instructions.step2.highlight')
                    }}</span>
                  </template>
                </Translation>
              </p>
            </div>

            <button
              class="w-full py-4 bg-gray-800 rounded-xl font-bold text-white"
              @click="closeGuide"
            >
              {{ t('iphone.gotIt') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<i18n>
{
  "en": {
    "install": {
      "title": "Install RCZ App",
      "description": "Enable push notifications & offline usage.",
      "prompt": "Install",
      "dismiss": "Dismiss"
    },
    "iphone": {
      "install": "Install on your iPhone",
      "instructions": {
        "step1": {
          "text": "Tap the {highlight} button in the browser toolbar.",
          "highlight": "Share"
        },
        "step2": {
          "text": "Scroll down and select {highlight}.",
          "highlight": "Add to Home Screen"
        }
      },
      "gotIt": "Got it"
    }
  },
  "de": {
    "install": {
      "title": "Installiere die RCZ App",
      "description": "Aktiviere Push-Benachrichtigungen & Offline-Nutzung.",
      "prompt": "Installieren",
      "dismiss": "Ausblenden"
    },
    "iphone": {
      "install": "Installiere auf deinem iPhone",
      "instructions": {
        "step1": {
          "text": "Klicke auf den {highlight}-Button im Browser",
          "highlight": "Teilen"
        },
        "step2": {
          "text": "Scrolle nach unten und wähle {highlight} aus.",
          "highlight": "Zum Home-Bildschirm hinzufügen"
        }
      },
      "gotIt": "Verstanden"
    }
  }
}
</i18n>
